/**
 * Runtime Configuration
 *
 * Provides environment variables at RUNTIME for the "build once, deploy everywhere" strategy.
 *
 * Problem: Modern.js `source.globalVars` replaces `process.env.X` at BUILD time,
 * baking values into the bundle. This means different environments (tst/stg/prd)
 * would all get the same values.
 *
 * Solution:
 * - Server-side: Read directly from `process.env` (Node.js runtime)
 * - Client-side: Read from `window.__RUNTIME_CONFIG__` (injected by SSR layout loader)
 *
 * Usage in components:
 *   import { useRuntimeConfig } from '@/config/runtimeConfig'
 *   const config = useRuntimeConfig()
 *   console.log(config.API_GATEWAY_URL)
 *
 * Usage in non-React code:
 *   import { getRuntimeConfig } from '@/config/runtimeConfig'
 *   const config = getRuntimeConfig()
 */

import { getApiGatewayUrl } from '@fe-toolkit/axios-api-toolkit'
import { createContext, useContext } from 'react'

import mfeConfigJson from '../../mfe.config.json'

/**
 * Build-time constant for the application base path.
 * Defined in mfe.config.json at the repo root — single source of truth.
 * Use this in components and server code instead of runtimeConfig.BASE_PATH.
 */
export const basePath = mfeConfigJson.basePath

/**
 * Build-time constant for the MFE name.
 * Defined in mfe.config.json at the repo root — single source of truth.
 * Use this in components and server code instead of runtimeConfig.MFE_NAME.
 */
export const mfeName = mfeConfigJson.mfeName

/**
 * Browser-safe runtime config keys.
 * These are serialized into the SSR HTML and exposed to the client via
 * `window.__RUNTIME_CONFIG__`. Only add keys that are safe to expose publicly.
 *
 * These are NOT baked into the build — they come from Kubernetes ConfigMap / env vars.
 */
export const BROWSER_CONFIG_KEYS = [
  'API_GATEWAY_URL',
  'CDN_URL',
  'HOST_URL',
  'GTM_ID',
  'OBSERVABILITY_BASE_URL',
  'ENABLE_TRACING',
  'ENVIRONMENT',
  'ENABLE_MOCKS',
  'NODE_ENV',
  'SNOWPLOW_COLLECTOR_URL',
  'ENABLE_SNOWPLOW',
  // Shows the CTC debug panel from ClientTrackingContextProvider. Runtime rather than
  // NODE_ENV so it can be switched on in staging, which runs a production build.
  'ENABLE_CTC_DEBUG_TOOLS',
  // Parent domain the __ggUtmz Portal bridge cookie is written on. Needed in the
  // browser as well as on the server: the SSR middleware cannot react to a
  // consent change that happens after the document was served, so the client
  // rewrites or expires the cookie on OneTrust consent transitions. See
  // src/utils/tracking/ggUtmzConsent.ts.
  'COOKIE_DOMAIN',
  // Public (ingress/CDN) mf-manifest.json URL of the fe-common Module Federation remote.
  // Resolved at runtime so the same build can point at TST/STG/PRD remotes — see
  // mf-runtime-remotes-plugin.ts, which overrides the build-time default in modern.config.ts.
  'FE_COMMON_REMOTE',
  // Selects which hero creative variant renders on the landing page (see
  // src/components/organisms/Hero/heroVariants.ts). Portal picked masthead creative
  // through a four-flag ladder (tariffRefresh2025 -> enableUnlimitedContractPromoHomePageBanner
  // -> blackFriday -> h2PromosHomepageBanner); this single runtime-config value replaces
  // that ladder so swapping campaign creative is a config change, not a code release.
  // Must go through runtime config, not source.globalVars, because this MFE is
  // build-once-deploy-everywhere and globalVars bakes values in at build time.
  'HERO_VARIANT',
  // FAAS-771: gates the root loader's logged-in -> /dashboard redirect (src/routes/page.data.ts).
  // Defaults to off in every environment — this route is a gated spike page (FAAS-750), and
  // redirecting the internal test group away from it would sabotage the FAAS-758 tracking
  // validation, which depends on the test group actually landing here.
  //
  // Browser-safe (not SERVER_-prefixed) despite only being read in a loader: loaders in this
  // app run client-side on subsequent SPA navigations, not just server-side on first load (see
  // docs/migrating-from-nextjs.md's loader rules), so a value read via getServerConfig() would
  // silently read as unset on every client-executed run — read via getRuntimeConfig() instead.
  // The value itself isn't sensitive, just a boolean toggle, so browser visibility is fine.
  // Set to 'true' to enable — see README.md's "Runtime Config Flags" section.
  'ENABLE_HOMEPAGE_DASHBOARD_REDIRECT',
  // FAAS-904: allows /sim-only-deals to be indexed (src/routes/sim-only-deals/simOnlyDealsLoader.ts).
  // Defaults to OFF (page stays noindex/nofollow) in every environment — this repo deploys every
  // push to main straight through tst/stg/prd, so removing the noindex flags in code cannot by
  // itself be timed against the separate Istio cutover (FAAS-903) that stops Portal serving these
  // same four paths. Flip to 'true' per environment only once that cutover is confirmed live;
  // flipping it before then recreates the duplicate-content problem noindex exists to prevent.
  'ENABLE_SIM_ONLY_DEALS_INDEXING',
  // FAAS-909: master switch for the portal-mfe-gate access gate in src/routes/layout.data.ts.
  // Defaults to OFF, so the gate is inert until an environment opts in. Off is deliberately
  // the safe default here rather than the strict one: this is a runtime kill switch, and the
  // thing it has to be able to do in a hurry is let traffic through when CTC cannot resolve
  // the flag, or when a tester needs a cold anonymous landing for the FAAS-758 campaign
  // scenarios. Flip it per environment in deployment/k8s/<env>/values.yaml; no rebuild.
  //
  // Browser-safe for the same reason as the flag above: loaders run client-side on SPA
  // navigations as well as server-side on first load, so a SERVER_-prefixed value would read
  // as unset on every client-executed run. The value is a boolean toggle, not a secret.
  'ENABLE_PORTAL_MFE_GATE',
  // FAAS-940: lets a homepage request that Portal routed here as a crawler past the gate
  // above. "true" serves it the page; anything else bounces every such request back to
  // Portal. That is the fast brake on the crawler flip, which the Eppo allocation does not
  // control. Inert until Portal's Apache rule starts marking requests.
  'ENABLE_HOMEPAGE_CRAWLERS_ON_MFE',
  // FAAS-940: the design those crawlers get. They skip CTC (server/modern.server.ts), so they
  // have no giffgaff-brand-refresh-2026 assignment of their own. "true" serves them the brand
  // refresh homepage; anything else, the Portal replica. Read by resolveIsBrandRefresh.
  'ENABLE_BRAND_REFRESH_FOR_CRAWLERS',
  // The Black Friday header navigation those crawlers get, for the same reason: no CTC, so no
  // black-friday-master / black-friday-prereg assignment. "true" turns each on. Read by
  // resolveLocalChromeFlags for the fallback header.
  'ENABLE_BLACK_FRIDAY_FOR_CRAWLERS',
  'ENABLE_BLACK_FRIDAY_PREREG_FOR_CRAWLERS',
  // The design when the server got no flags at all from CTC (an outage): "true" is Brand Refresh,
  // anything else legacy. Covers the homepage, fe-common's header and footer (through
  // resolveIsBrandRefresh) and the fallback header and footer. This app's counterpart to Portal's
  // giffgaffBrandRefresh2026HeaderFooter.
  'ENABLE_BRAND_REFRESH_WITHOUT_CTC',
  // Master switch for the giffgaff-brand-refresh-2026 gate, which is a different gate from
  // the one above and has a different job. portal-mfe-gate decides who gets the homepage
  // traffic split; this one keeps the extraction pages (/sim-only-deals,
  // /existing-why-giffgaff) unpublished while they are still being built. Separate switches
  // because a homepage ramp must not withdraw pages that are already live to a test group.
  'ENABLE_BRAND_REFRESH_GATE',
  // FAAS-779: content for the top-of-page campaign promo bar (see
  // src/components/organisms/CampaignPromoBar/campaignPromoConfig.ts). This replaces
  // marketing's current GTM-DOM-surgery workflow (the "CMS - New Homepage Masthead" tag),
  // so the copy, terms link and CTA destination all have to be swappable per environment
  // with no rebuild. Each is its own key, rather than one JSON blob, to stay consistent
  // with every other value in this file being a plain string.
  'CAMPAIGN_PROMO_HEADLINE',
  'CAMPAIGN_PROMO_TERMS_LABEL',
  'CAMPAIGN_PROMO_TERMS_HREF',
  'CAMPAIGN_PROMO_CTA_LABEL',
  'CAMPAIGN_PROMO_CTA_HREF',
  // FAAS-872: SKUs whose /sim-only-deals CTA uses Portal's newer checkout URL format.
  // Portal reads this from `goodybags.goodybagsNewURLFormat` in configuration-svc and splits
  // it on commas; production is "BD078,BD081" today. Comma-separated here for the same
  // reason, so the two sides stay diffable.
  //
  // Config rather than an Eppo flag on purpose: it is a value, not a boolean, and it changes
  // rarely. Flagged on FAAS-872 so nobody adds it to Eppo by pattern-matching the three promo
  // toggles that ticket does move.
  //
  // Browser-safe for the same reason as the two toggles above: loaders re-run client-side on
  // SPA navigation, so a SERVER_-prefixed value would read as unset on those runs. A list of
  // public SKUs is not sensitive.
  'GOODYBAGS_NEW_URL_FORMAT',
  // The merchandising tags fe-devices labels Black Friday and member exclusive deals by, so the
  // brand refresh phone card labels the same phones the range page does. Values match
  // fe-devices' deployment/k8s/<env>/values.yaml; unset matches no phone. Browser-safe for the
  // same reason as the keys above: getHomepagePhones also runs in client-executed loaders.
  'BLACK_FRIDAY_TAG',
  'MEMBER_EXCLUSIVE_TAG',
] as const

/**
 * Server-only config keys.
 * These are available on the server via `process.env` but are NEVER serialized
 * into the SSR HTML or exposed to the browser. Add internal/sensitive URLs here.
 */
export const SERVER_ONLY_CONFIG_KEYS = [
  'SERVER_OBSERVABILITY_BASE_URL',
  // Internal (cluster-DNS) mf-manifest.json URL of the fe-common remote, used by the
  // SSR server only. The browser uses FE_COMMON_REMOTE instead. Falls back to the
  // public URL when unset — see mf-runtime-remotes-plugin.ts.
  'SERVER_FE_COMMON_REMOTE',
  // In-cluster (service DNS) address of the same API gateway the browser reaches via
  // API_GATEWAY_URL. Used by SSR loaders only, to skip the hairpin out through the
  // WAF/ingress and back. Same gateway, same auth and CSRF semantics. Falls back to
  // API_GATEWAY_URL when unset — see getApiGatewayBaseUrl() below.
  //
  // The address itself is deliberately not written here: this module is part of the
  // client graph, and production builds publish separate .js.map files, so anything in
  // this file's source text is publicly readable. See deployment/k8s/<env>/values.yaml
  // for the value, and docs/api-integration-guide.md for the rationale.
  'SERVER_API_GATEWAY_URL',
  // In-cluster base URL of the phones CMS, which serves the homepage promotion banners
  // Portal renders (FAAS-774). Server-only by necessity, not just by policy: the CMS has no
  // public gateway route, so the browser could not reach it and publishing the address would
  // leak internal topology for nothing. Unset renders no promotion banners — see
  // src/api/phones-cms/getHomepagePromotions.ts.
  'SERVER_PHONES_CMS_URL',
  // In-cluster address of client-tracking-context-svc, which fe-common's resolveChromeFlags reads
  // the header and footer flags from during SSR. Unset leaves the chrome to fe-common's browser
  // lookup. See src/api/client-tracking-context/getChromeFlags.ts.
  'SERVER_CTC_SERVICE_URL',
] as const

/**
 * All runtime config keys (server + browser).
 */
export const RUNTIME_CONFIG_KEYS = [...BROWSER_CONFIG_KEYS, ...SERVER_ONLY_CONFIG_KEYS] as const

/** Union of browser-safe runtime config key names. */
type BrowserConfigKey = (typeof BROWSER_CONFIG_KEYS)[number]

/** Union of server-only runtime config key names. */
export type ServerOnlyConfigKey = (typeof SERVER_ONLY_CONFIG_KEYS)[number]

/** Union of all runtime config key names (browser + server). */
type RuntimeConfigKey = (typeof RUNTIME_CONFIG_KEYS)[number]

/** Browser-safe runtime config object — all values are strings. */
export type RuntimeConfig = Record<BrowserConfigKey, string>

/** Full runtime config including server-only keys. */
export type FullRuntimeConfig = Record<RuntimeConfigKey, string>

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig
  }
}

/**
 * Build the browser-safe runtime config from the current environment.
 * Called server-side in the layout loader to serialize into the SSR response.
 * Only includes keys from BROWSER_CONFIG_KEYS — server-only values are excluded.
 */
export function buildRuntimeConfig(): RuntimeConfig {
  return BROWSER_CONFIG_KEYS.reduce(
    (acc, key) => {
      acc[key] = process.env[key] || ''

      return acc
    },
    {} as Record<string, string>
  ) as RuntimeConfig
}

/**
 * Safely serialize a value for embedding in an inline `<script>` tag.
 *
 * Used for both `window.__RUNTIME_CONFIG__` and the initial dataLayer push in
 * `src/routes/layout.tsx`; the escaping requirement is identical for any value.
 *
 * Plain `JSON.stringify` is NOT safe inside `<script>` because:
 * - A value containing `</script>` closes the script tag, enabling XSS.
 * - `\u2028` / `\u2029` are valid JSON but are line terminators in JS,
 *   which break string literals in older engines (pre-ES2019).
 *
 * This function replaces every `<`, `\u2028`, and `\u2029` character with its
 * `\uXXXX` Unicode escape in a single pass. On Node 22+ (ES2019+),
 * `JSON.stringify` already escapes `\u2028`/`\u2029`, so only `<` will match
 * in practice — the regex is kept defensive for portability.
 */
export function serializeConfigForScript(value: object): string {
  return JSON.stringify(value).replace(/[<\u2028\u2029]/g, (char) => {
    return `\\u${char.charCodeAt(0).toString(16).padStart(4, '0')}`
  })
}

/**
 * Get the runtime config from the appropriate source.
 *
 * - Server: Reads from process.env
 * - Client: Reads from window.__RUNTIME_CONFIG__ (set by SSR)
 */
export function getRuntimeConfig(): RuntimeConfig {
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) {
    return window.__RUNTIME_CONFIG__
  }

  // Server-side: read from process.env (Node.js runtime).
  // In the browser this is a no-op fallback — all values default to '' — because
  // window.__RUNTIME_CONFIG__ is set by an inline <script> in the SSR HTML.
  return buildRuntimeConfig()
}

/**
 * Read a server-only config value.
 *
 * Returns undefined in the browser by construction: SERVER_ONLY_CONFIG_KEYS are never
 * serialized into window.__RUNTIME_CONFIG__, so there is nothing there to read.
 *
 * An empty string is normalised to undefined so that `||` fallbacks behave identically
 * whether a key is absent or blank — Kubernetes values files use "" to mean "not
 * configured" (see the SERVER_FE_COMMON_REMOTE entries in deployment/k8s/local).
 *
 * Note the computed `process.env[key]` access, matching buildRuntimeConfig(). A static
 * `process.env.SERVER_API_GATEWAY_URL` member expression could be substituted at build
 * time by a bundler; computed access cannot, which keeps internal hostnames out of
 * client chunks regardless of how source.globalVars evolves.
 */
export function getServerConfig(key: ServerOnlyConfigKey): string | undefined {
  if (typeof window !== 'undefined') {
    return undefined
  }

  return process.env[key] || undefined
}

/** Fallback gateway URL for local development when nothing is configured. */
export const DEFAULT_API_GATEWAY_URL = 'http://localhost:8081'

/**
 * Resolve the API gateway base URL for the current execution context.
 *
 * - Browser → API_GATEWAY_URL, the public gateway, so traffic passes the WAF/ingress.
 * - Server  → SERVER_API_GATEWAY_URL when set, otherwise API_GATEWAY_URL.
 *
 * Both terminate at the same gateway with the same auth and CSRF semantics; the server
 * variant just uses in-cluster DNS, skipping the hairpin out through the ingress and
 * back. The fallback ordering mirrors createRuntimeRemotesPlugin (see
 * mf-runtime-remotes-plugin.ts), so an environment that does not set the server variant
 * keeps working unchanged — which also makes rollback config-only.
 *
 * The in-cluster address is origin-only. The public URL carries /gateway only because
 * the nginx ingress rewrites it away before the request reaches the gateway service,
 * whose own context-path is /. Repeating that prefix internally 404s every request.
 *
 * Branches on `typeof window`, not on whether window.__RUNTIME_CONFIG__ exists: under
 * jsdom in unit tests `window` is defined but the config is not, and those tests must
 * take the browser branch.
 */
export function getApiGatewayBaseUrl(): string {
  return getApiGatewayUrl({
    isServer: typeof window === 'undefined',
    serverApiGatewayUrl: getServerConfig('SERVER_API_GATEWAY_URL'),
    apiGatewayUrl: getRuntimeConfig().API_GATEWAY_URL || DEFAULT_API_GATEWAY_URL,
  })
}

/**
 * Resolve the phones CMS base URL from SERVER_PHONES_CMS_URL, without a trailing slash, or
 * undefined when it is unset or blank. No default: unset means no banners, not a guessed URL.
 * Server-only, like the key itself; see getHomepagePromotions for why.
 */
export function getPhonesCmsBaseUrl(): string | undefined {
  return getServerConfig('SERVER_PHONES_CMS_URL')?.replace(/\/+$/, '') || undefined
}

/**
 * React Context for runtime configuration.
 * Provided by the root layout, available to all components.
 */
const RUNTIME_CONFIG_DEFAULT = {} as RuntimeConfig

export const RuntimeConfigContext = createContext<RuntimeConfig>(RUNTIME_CONFIG_DEFAULT)
/**
 * Hook to access runtime configuration in React components.
 *
 * @example
 * ```tsx
 * const config = useRuntimeConfig()
 * console.log(config.API_GATEWAY_URL)
 * ```
 */
export function useRuntimeConfig(): RuntimeConfig {
  return useContext(RuntimeConfigContext)
}
